/**
 * 수납결의 공통 워크스페이스
 * - register: 승인된 수입결의 선택 · 수납정보 입력
 * - approve: 승인요청 목록 승인/반려
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { ActiveGridHeader } from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  STATUS,
  STATUS_OPTIONS,
  createReceiptUid,
  emptyReceiptMaster,
  loadReceiptBundle,
  saveReceiptBundle,
  loadReceiptCandidates,
  filterReceiptsForRegister,
  filterReceiptsForApprove,
  requestReceiptApproval,
  approveReceipt,
  rejectReceipt,
  nextReceiptNo,
  statusLabel,
  todayYmd,
} from 'income/incomeStore';

const LIST_GRID_ID = 'income-receipt-list';
const APPROVE_GRID_ID = 'income-receipt-approve';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || 1};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

function emptySearch(partial = {}) {
  return { fiscalYear: getCurrentFiscalYear(), status: '', ...partial };
}

/**
 * @param {{ screenMode?: 'register'|'approve' }} props
 */
export default function ReceiptResolutionWorkspace({ screenMode = 'register' }) {
  const isApprove = screenMode === 'approve';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const searchDefaults = useMemo(
    () => emptySearch({ status: isApprove ? STATUS.REQUESTED : '' }),
    [isApprove]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...searchDefaults,
      ...emptyReceiptMaster({ receiptDate: todayYmd(), fiscalYear: searchDefaults.fiscalYear }),
      incomeResolutionPicker: '',
    },
  });
  const { getValues, reset, setValue, control } = methods;

  const listRef = useRef(null);
  const approveRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [resolutionOptions, setResolutionOptions] = useState([]);

  const masterStatus = useWatch({ control, name: 'status' });
  const masterReadonly =
    isApprove ||
    (masterStatus && masterStatus !== STATUS.DRAFT && masterStatus !== STATUS.REJECTED);

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const dimsFromSearch = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const enrichRow = useCallback(
    (row) => ({
      ...row,
      statusNm: statusLabel(row.status),
    }),
    []
  );

  const loadResolutionPicker = useCallback(async () => {
    const dims = dimsFromSearch();
    const candidates = await loadReceiptCandidates(dims);
    setResolutionOptions(candidates);
  }, [dimsFromSearch]);

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '수납번호', field: 'receiptNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '수입결의', field: 'incomeResolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roNumberColumn({ header: '수납금액', field: 'receiptAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  const approveColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '수납번호', field: 'receiptNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '수입결의', field: 'incomeResolutionNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 90 }),
      hiCol.roNumberColumn({ header: '수납금액', field: 'receiptAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '은행', field: 'bankName', size: 90 }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  const listGrid = useHiGridHook({
    gridRef: listRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const approveGrid = useHiGridHook({
    gridRef: approveRef,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const loadMasterToForm = useCallback(
    (row) => {
      if (!row) {
        reset({
          ...getValues(),
          ...emptyReceiptMaster({ fiscalYear: getValues('fiscalYear'), receiptDate: todayYmd() }),
          incomeResolutionPicker: '',
        });
        return;
      }
      reset({
        ...getValues(),
        ...row,
        statusNm: statusLabel(row.status),
        incomeResolutionPicker: row.incomeResolutionUid || '',
      });
    },
    [reset, getValues]
  );

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    await loadResolutionPicker();

    const bundle = await loadReceiptBundle(dims);
    const statusFilter = String(getValues('status') || '').trim();

    if (isApprove) {
      let masters = filterReceiptsForApprove(bundle.masters, { status: statusFilter || undefined });
      masters = masters.map(enrichRow);
      approveRef.current?.setData?.(masters);
      return;
    }

    let masters = filterReceiptsForRegister(bundle.masters, { status: statusFilter || undefined });
    masters = masters.map(enrichRow);
    listRef.current?.setData?.(masters);
    const first = masters[0];
    setSelectedUid(first?.receiptUid || '');
    loadMasterToForm(first);
  }, [dimsFromSearch, getValues, isApprove, enrichRow, loadMasterToForm, loadResolutionPicker]);

  useEffect(() => {
    reset({
      ...searchDefaults,
      ...emptyReceiptMaster({ fiscalYear: searchDefaults.fiscalYear, receiptDate: todayYmd() }),
    });
  }, [reset, searchDefaults]);

  const onListSelect = useCallback(
    (row) => {
      setSelectedUid(row?.receiptUid || '');
      loadMasterToForm(row);
    },
    [loadMasterToForm]
  );

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => onListSelect(data);
    listGrid.onSelectRow = ({ data }) => onListSelect(data);
  }, [listGrid, onListSelect]);

  useEffect(() => {
    approveGrid.onRowClicked = ({ data }) => {
      selectedUidRef.current = data?.receiptUid || '';
      setSelectedUid(data?.receiptUid || '');
    };
    approveGrid.onSelectRow = ({ data }) => {
      selectedUidRef.current = data?.receiptUid || '';
      setSelectedUid(data?.receiptUid || '');
    };
  }, [approveGrid]);

  const handleNew = useCallback(() => {
    if (isApprove) return;
    const uid = createReceiptUid();
    const row = enrichRow(
      emptyReceiptMaster({
        receiptUid: uid,
        fiscalYear: dimsFromSearch().fiscalYear,
        receiptDate: todayYmd(),
        status: STATUS.DRAFT,
        registerDate: todayYmd(),
        mod: 'A',
      })
    );
    setSelectedUid(uid);
    selectedUidRef.current = uid;
    reset({ ...getValues(), ...row, incomeResolutionPicker: '' });
  }, [isApprove, enrichRow, dimsFromSearch, reset, getValues]);

  const handleSave = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 저장할 수 없습니다.');
      return;
    }
    const dims = dimsFromSearch();
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createReceiptUid();
      setSelectedUid(uid);
      selectedUidRef.current = uid;
    }

    const form = getValues();
    const bundle = await loadReceiptBundle(dims);

    let masterDraft = {
      ...emptyReceiptMaster(),
      ...form,
      receiptUid: uid,
      fiscalYear: dims.fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      status: form.status || STATUS.DRAFT,
      mod: form.mod || 'U',
    };

    if (!masterDraft.receiptNo) {
      masterDraft.receiptNo = nextReceiptNo(bundle.masters);
    }

    const picker = String(form.incomeResolutionPicker || '').trim();
    if (picker) {
      const res = resolutionOptions.find((o) => o.value === picker);
      if (res) {
        masterDraft = {
          ...masterDraft,
          incomeResolutionUid: res.resolutionUid,
          incomeResolutionNo: res.resolutionNo,
          deptCode: res.deptCode,
          deptName: res.deptName,
          projectCode: res.projectCode,
          projectName: res.projectName,
          subjectCode: res.subjectCode,
          subjectName: res.subjectName,
          fundCode: res.fundCode,
          fundName: res.fundName,
          receiptAmt: masterDraft.receiptAmt || res.remainAmt || res.resolutionAmt || 0,
        };
      }
    }

    let masters = [...(bundle.masters || [])];
    const idx = masters.findIndex((m) => m.receiptUid === uid);
    if (idx >= 0) masters[idx] = { ...masters[idx], ...masterDraft, mod: 'U' };
    else masters.push({ ...masterDraft, mod: 'A' });

    const saved = await saveReceiptBundle(dims, masters);
    listRef.current?.setData?.(saved.masters.filter((m) => m.mod !== 'D').map(enrichRow));
    loadMasterToForm(saved.masters.find((m) => m.receiptUid === uid));
    showAlert('저장했습니다.', 'success');
  }, [isApprove, dimsFromSearch, getValues, resolutionOptions, enrichRow, loadMasterToForm]);

  const handleRequest = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인요청할 수납결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadReceiptBundle(dims);
    const nextM = requestReceiptApproval(bundle.masters, uid);
    await saveReceiptBundle(dims, nextM);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [isApprove, handleSave, dimsFromSearch, handleSearch]);

  const handleApprove = useCallback(async () => {
    if (!isApprove) return;
    const rows = approveRef.current?.getData?.() || [];
    const checked = approveRef.current?.getCheckedData?.() || [];
    const target = checked[0] || rows.find((r) => r.receiptUid === selectedUidRef.current);
    if (!target?.receiptUid) {
      showAlert('승인할 수납결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('승인할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadReceiptBundle(dims);
    const nextM = approveReceipt(bundle.masters, target.receiptUid);
    await saveReceiptBundle(dims, nextM);
    await handleSearch();
    showAlert('승인했습니다. (전표 stub)', 'success');
  }, [isApprove, dimsFromSearch, handleSearch]);

  const handleReject = useCallback(async () => {
    if (!isApprove) return;
    const rows = approveRef.current?.getData?.() || [];
    const checked = approveRef.current?.getCheckedData?.() || [];
    const target = checked[0] || rows.find((r) => r.receiptUid === selectedUidRef.current);
    if (!target?.receiptUid) {
      showAlert('반려할 수납결의를 선택하세요.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadReceiptBundle(dims);
    const nextM = rejectReceipt(bundle.masters, target.receiptUid);
    await saveReceiptBundle(dims, nextM);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isApprove, dimsFromSearch, handleSearch]);

  const onResolutionPick = useCallback(
    (e) => {
      const val = e?.target?.value ?? e;
      const res = resolutionOptions.find((o) => o.value === val);
      if (!res) return;
      setValue('incomeResolutionUid', res.resolutionUid);
      setValue('incomeResolutionNo', res.resolutionNo);
      setValue('deptCode', res.deptCode);
      setValue('deptName', res.deptName);
      setValue('projectCode', res.projectCode);
      setValue('projectName', res.projectName);
      setValue('subjectCode', res.subjectCode);
      setValue('subjectName', res.subjectName);
      setValue('fundCode', res.fundCode);
      setValue('fundName', res.fundName);
      if (!getValues('receiptAmt')) {
        setValue('receiptAmt', res.remainAmt || res.resolutionAmt || 0);
      }
    },
    [resolutionOptions, setValue, getValues]
  );

  const searchFields = useMemo(
    () => [
      [
        { name: 'fiscalYear', label: '회계연도', type: 'fiscalYear', width: '88px', labelWidth: SEARCH_LABEL_W },
        {
          name: 'status',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...STATUS_OPTIONS],
          width: '120px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const masterFields = useMemo(
    () => [
      [
        { name: 'receiptNo', label: '수납번호', type: 'text', readOnly: true, width: '90px', labelWidth: '64px' },
        {
          name: 'incomeResolutionPicker',
          label: '수입결의',
          type: 'select',
          options: [{ value: '', label: '(선택)' }, ...resolutionOptions.map((o) => ({ value: o.value, label: o.label }))],
          width: '240px',
          labelWidth: '64px',
          readOnly: masterReadonly,
          onChange: onResolutionPick,
        },
      ],
      [
        { name: 'deptName', label: '부서', type: 'text', readOnly: true, width: '110px', labelWidth: '64px' },
        { name: 'projectName', label: '사업', type: 'text', readOnly: true, width: '120px', labelWidth: '48px' },
        { name: 'subjectName', label: '과목', type: 'text', readOnly: true, width: '120px', labelWidth: '48px' },
        { name: 'fundName', label: '재원', type: 'text', readOnly: true, width: '90px', labelWidth: '48px' },
      ],
      [
        {
          name: 'receiptAmt',
          label: '수납금액',
          type: 'number',
          width: '120px',
          labelWidth: '64px',
          readOnly: masterReadonly,
        },
        {
          name: 'receiptDate',
          label: '수납일',
          type: 'date',
          width: '120px',
          labelWidth: '48px',
          readOnly: masterReadonly,
        },
        {
          name: 'bankName',
          label: '은행',
          type: 'text',
          width: '100px',
          labelWidth: '48px',
          readOnly: masterReadonly,
        },
        {
          name: 'accountNo',
          label: '계좌',
          type: 'text',
          width: '140px',
          labelWidth: '48px',
          readOnly: masterReadonly,
        },
        { name: 'statusNm', label: '상태', type: 'text', readOnly: true, width: '80px', labelWidth: '48px' },
      ],
      [
        {
          name: 'remark',
          label: '적요',
          type: 'text',
          width: '400px',
          labelWidth: '64px',
          readOnly: masterReadonly,
        },
      ],
    ],
    [resolutionOptions, masterReadonly, onResolutionPick]
  );

  const actionButtons = useMemo(() => {
    const base = [{ id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' }];
    if (isApprove) return base;
    return [
      ...base,
      { id: 'new', label: '추가', icon: '➕', onClick: handleNew, hotkey: 'F8' },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    ];
  }, [isApprove, handleSearch, handleNew, handleSave]);

  const extraButtons = useMemo(() => {
    if (isApprove) {
      return [
        { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
        { id: 'reject', label: '반려', onClick: handleReject, variant: 'danger' },
      ];
    }
    return [{ id: 'request', label: '승인요청', onClick: handleRequest }];
  }, [isApprove, handleApprove, handleReject, handleRequest]);

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel
            storageKey={`income-receipt-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            {isApprove
              ? '승인요청 수납결의 선택 후 승인/반려'
              : '승인된 수입결의 중 미수납·부분수납 건 선택'}
          </Hint>

          {isApprove ? (
            <Panel $flex={1.5}>
              <ActiveGridHeader gridId={APPROVE_GRID_ID}>수납결의 승인</ActiveGridHeader>
              <GridBody>
                <HiGridComponent
                  ref={approveRef}
                  columns={approveColumns}
                  showRowNumber
                  rowNumberSize={48}
                  enableColumnFilters
                  enableHeaderMenu
                  enableQuickExport
                  exportFileName="수납결의승인"
                  gridId={`income-receipt-approve-${screenMode}`}
                  prefsUserId={prefsUserId}
                />
              </GridBody>
            </Panel>
          ) : (
            <>
              <Panel $flex={0.9}>
                <ActiveGridHeader gridId={LIST_GRID_ID}>수납결의 목록</ActiveGridHeader>
                <GridBody>
                  <HiGridComponent
                    ref={listRef}
                    columns={listColumns}
                    showRowNumber
                    rowNumberSize={48}
                    enableColumnFilters
                    enableHeaderMenu
                    gridId={`income-receipt-list-${screenMode}`}
                    prefsUserId={prefsUserId}
                  />
                </GridBody>
              </Panel>

              <Panel $flex={0.7}>
                <ActiveGridHeader gridId="receipt-master">수납결의 마스터</ActiveGridHeader>
                <FormArea dense fields={masterFields} />
              </Panel>
            </>
          )}
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}
